import React from 'react';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import styles from './LessonContentRenderer.module.css';

interface AnimationPlaceholderProps {
    description: string;
    lottieUrl?: string;
}

const AnimationPlaceholder: React.FC<AnimationPlaceholderProps> = ({ description, lottieUrl }) => {
    // Determine type based on description keywords
    const isInfographic = description.toLowerCase().includes('infographic');
    const isSlideTransition = description.toLowerCase().includes('slide transition');
    const isNextLessonPreview = description.toLowerCase().includes('next lesson preview');

    if (typeof lottieUrl === 'string' && lottieUrl.length > 0) {
        return (
            <motion.div
                className={`${styles.animationPlaceholder} ${styles.lottieContainer}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className={styles.lottieWrapper}>
                    <DotLottieReact
                        src={lottieUrl}
                        autoplay
                        loop
                        className={styles.lottiePlayer}
                    />
                </div>
                <div className={styles.animationContent}>
                    <span className={styles.animationLabel}>Interactive Animation</span>
                    <span className={styles.animationDescription}>{description}</span>
                </div>
            </motion.div>
        );
    }


    if (isSlideTransition) {
        return (
            <motion.div
                className={styles.slideSectionBreak}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: false, amount: 0.5 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className={styles.slideDividerLine} />
                <span className={styles.slideIcon}>❖</span>
                <span className={styles.slideLabel}>Next Concept</span>
                <div className={styles.slideDividerLine} />
            </motion.div>
        );
    }

    if (isNextLessonPreview) {
        return (
            <motion.div
                className={styles.nextLessonPreview}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.2 }}
                transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className={styles.previewLabel}>COMING UP NEXT</div>
                <div className={styles.previewTitle}>The Risks of Driving</div>

                <div className={styles.previewComplianceBox}>
                    <div className={styles.previewComplianceIcon}>⏳</div>
                    <div className={styles.previewComplianceText}>
                        <strong>Minimum Time Requirement Active</strong>
                        <p>State law requires spending the full duration on this lesson.</p>
                        <p className={styles.previewSuggestion}>
                            If you have time remaining, please review the material above to ensure absorption.
                        </p>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            className={`${styles.animationPlaceholder} ${isInfographic ? styles.infographic : ''}`}
            initial={{ opacity: 0, scale: 1.05 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className={styles.animationIcon}>
                {isInfographic ? '📊' : '🎬'}
            </div>
            <div className={styles.animationContent}>
                <span className={styles.animationLabel}>
                    {isInfographic ? 'Visual Placeholder' : 'Scene Transition'}
                </span>
                <span className={styles.animationDescription}>{description}</span>
            </div>
        </motion.div>
    );
};

export default AnimationPlaceholder;
